/**
 * Standalone Tasks Module (Tapşırıqlar) — top-level panel.
 * Dynamic stages from /api/task-stages + approval checkboxes + SuperAdmin god mode.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Plus, Calendar, AlertCircle, Search, ListChecks, Tag as TagIcon, Trash2,
  User as UserIcon, X, ShieldCheck, CheckSquare, MessageSquare, Info, Archive,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_MAP = {
  zinc:    { col: 'bg-zinc-50 border-zinc-300',       hdr: 'text-zinc-700',    over: 'bg-zinc-100',    dot: 'bg-zinc-400' },
  blue:    { col: 'bg-blue-50 border-blue-300',       hdr: 'text-blue-700',    over: 'bg-blue-100',    dot: 'bg-blue-500' },
  amber:   { col: 'bg-amber-50 border-amber-300',     hdr: 'text-amber-700',   over: 'bg-amber-100',   dot: 'bg-amber-500' },
  red:     { col: 'bg-red-50 border-red-300',         hdr: 'text-red-700',     over: 'bg-red-100',     dot: 'bg-red-500' },
  emerald: { col: 'bg-emerald-50 border-emerald-300', hdr: 'text-emerald-700', over: 'bg-emerald-100', dot: 'bg-emerald-500' },
  purple:  { col: 'bg-purple-50 border-purple-300',   hdr: 'text-purple-700',  over: 'bg-purple-100',  dot: 'bg-purple-500' },
  pink:    { col: 'bg-pink-50 border-pink-300',       hdr: 'text-pink-700',    over: 'bg-pink-100',    dot: 'bg-pink-500' },
  orange:  { col: 'bg-orange-50 border-orange-300',   hdr: 'text-orange-700',  over: 'bg-orange-100',  dot: 'bg-orange-500' },
  cyan:    { col: 'bg-cyan-50 border-cyan-300',       hdr: 'text-cyan-700',    over: 'bg-cyan-100',    dot: 'bg-cyan-500' },
};

const styleOf = (c) => COLOR_MAP[c] || COLOR_MAP.zinc;

const PRIORITY = {
  low: { label: 'Aşağı', dot: 'bg-zinc-400' },
  medium: { label: 'Orta', dot: 'bg-amber-400' },
  high: { label: 'Yüksək', dot: 'bg-red-500' },
};

const SCOPES = [
  { key: 'mine', label: 'Mənim tapşırıqlarım' },
  { key: 'assigned', label: 'Mənə təyin olunmuşlar' },
  { key: 'created', label: 'Yaratdıqlarım' },
  { key: 'all', label: 'Hamısı' },
];

function isOverdue(due_date, statusKey, doneKeys) {
  if (!due_date || doneKeys.has(statusKey)) return false;
  return new Date(due_date) < new Date();
}

function formatShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('az-AZ', { day: 'numeric', month: 'short' });
}

// ==================== Card ====================
function TaskCard({ task, index, usersMap, onClick, doneKeys }) {
  const assignees = (task.assignee_ids || []).map((id) => usersMap.get(id)).filter(Boolean);
  const overdue = isOverdue(task.due_date, task.status, doneKeys);
  const approvers = task.approvers || [];
  const approvedCount = approvers.filter((a) => a.status === 'approved').length;

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => !snapshot.isDragging && onClick?.(task)}
          style={{ ...provided.draggableProps.style }}
          className={`bg-white border rounded-md p-3 mb-2 transition-shadow cursor-grab active:cursor-grabbing ${
            snapshot.isDragging
              ? 'shadow-2xl ring-2 ring-purple-400 scale-[1.02] rotate-1'
              : 'shadow-sm hover:shadow-md'
          }`}
          data-testid={`task-card-${task.id}`}
        >
          <div className="flex items-start gap-2">
            <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${PRIORITY[task.priority]?.dot || 'bg-zinc-300'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium leading-snug">{task.title}</div>
              {task.description && (
                <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{task.description}</div>
              )}
            </div>
          </div>
          {(task.tags || []).length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {task.tags.slice(0, 3).map((t) => (
                <Badge key={t} variant="secondary" className="text-[10px] py-0 px-1.5">{t}</Badge>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex -space-x-1.5">
              {assignees.slice(0, 3).map((a) => (
                <div
                  key={a.id}
                  title={a.full_name || a.username}
                  className="h-6 w-6 rounded-full bg-purple-100 border-2 border-white text-[10px] font-semibold flex items-center justify-center text-purple-700"
                >
                  {(a.full_name || a.username).slice(0, 2).toUpperCase()}
                </div>
              ))}
              {assignees.length > 3 && (
                <div className="h-6 w-6 rounded-full bg-zinc-100 border-2 border-white text-[10px] font-semibold flex items-center justify-center text-zinc-600">
                  +{assignees.length - 3}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {approvers.length > 0 && (
                <div
                  className={`flex items-center gap-0.5 text-[10px] ${
                    approvedCount === approvers.length ? 'text-emerald-600' : 'text-amber-600'
                  }`}
                  title={`${approvedCount} / ${approvers.length} təsdiq`}
                  data-testid={`task-approval-badge-${task.id}`}
                >
                  <ShieldCheck className="h-3 w-3" />
                  {approvedCount}/{approvers.length}
                </div>
              )}
              {task.due_date && (
                <div
                  className={`flex items-center gap-1 text-[11px] ${
                    overdue ? 'text-red-600 font-semibold' : 'text-zinc-500'
                  }`}
                  data-testid={`task-due-${task.id}`}
                >
                  {overdue ? <AlertCircle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
                  {formatShort(task.due_date)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

// ==================== Column ====================
function Column({ stage, tasks, usersMap, canAdd, onAdd, onCardClick, doneKeys }) {
  const s = styleOf(stage.color);
  return (
    <div
      className={`flex flex-col rounded-lg border-2 ${s.col} min-h-[420px]`}
      data-testid={`task-col-${stage.key}`}
    >
      <div className="px-3 py-2.5 border-b flex items-center justify-between sticky top-0 bg-inherit">
        <div className={`text-sm font-semibold ${s.hdr} flex items-center gap-1.5`}>
          {stage.name}
          {stage.is_approval_gate && <ShieldCheck className="h-3.5 w-3.5 opacity-70" />}
          <span className="ml-1 text-xs text-zinc-500 font-normal">{tasks.length}</span>
        </div>
        {canAdd && (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onAdd(stage.key)}
            title="Yeni tapşırıq"
            data-testid={`add-task-${stage.key}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
      <Droppable droppableId={stage.key}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 overflow-y-auto transition-colors duration-150 ${
              snapshot.isDraggingOver ? `${s.over} ring-2 ring-inset ring-purple-300` : ''
            }`}
          >
            {tasks.length === 0 && !snapshot.isDraggingOver && (
              <div className="text-xs text-zinc-400 italic text-center py-6">Boşdur</div>
            )}
            {tasks.map((t, idx) => (
              <TaskCard key={t.id} task={t} index={idx} usersMap={usersMap} onClick={onCardClick} doneKeys={doneKeys} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ==================== Approval Action Panel (v2 — single button) ====================
function ApprovalActionPanel({ taskId, currentUser, usersMap, onAction }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tasks/${taskId}/approval-info`, { headers });
      setInfo(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [taskId, headers]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleApprove = async () => {
    const note = window.prompt('Təsdiq qeydi (isteğe bağlı):', '');
    if (note === null) return;
    setActionLoading(true);
    try {
      const r = await axios.post(`${API}/tasks/${taskId}/approve`, { note }, { headers });
      toast.success(r.data?.fully_done
        ? '✅ Tapşırıq tamamlandı və arxivləndi'
        : `✓ Təsdiqləndi — növbəti mərhələ: ${r.data?.next_level || 2}`);
      onAction?.();
      fetchInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Təsdiq uğursuz');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Rədd səbəbi (məcburi):', '');
    if (!reason || !reason.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/tasks/${taskId}/reject`, { reason: reason.trim() }, { headers });
      toast.success('❌ Rədd edildi, tapşırıq İcrada-ya qayıtdı');
      onAction?.();
      fetchInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Uğursuz');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !info) {
    return <div className="text-xs text-zinc-500 py-4">Yüklənir...</div>;
  }

  if (!info.in_approval_gate) {
    return (
      <div className="border-t pt-3" data-testid="approval-panel">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <ShieldCheck className="h-4 w-4 opacity-50" />
          Bu tapşırıq hazırda təsdiq mərhələsində deyil
        </div>
      </div>
    );
  }

  const stages = info.approval_stages || [];
  const isSA = currentUser?.role === 'superadmin' || currentUser?.is_superadmin;

  return (
    <div className="border-t pt-3 space-y-3" data-testid="approval-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-medium">Təsdiq axını</span>
      </div>

      {/* Stage progress */}
      <div className="space-y-2">
        {stages.map((s) => {
          const u = s.approved_by ? usersMap.get(s.approved_by) : null;
          const isCurrent = info.current_stage?.level === s.level;
          return (
            <div
              key={s.level}
              className={`flex items-center gap-2 p-2.5 rounded-md border ${
                s.status === 'approved' ? 'bg-emerald-50 border-emerald-200'
                  : s.status === 'rejected' ? 'bg-red-50 border-red-200'
                  : isCurrent ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200'
                  : 'bg-zinc-50'
              }`}
              data-testid={`approval-stage-${s.level}`}
            >
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                s.status === 'approved' ? 'bg-emerald-500'
                  : s.status === 'rejected' ? 'bg-red-500'
                  : isCurrent ? 'bg-amber-500'
                  : 'bg-zinc-300'
              }`}>
                {s.status === 'approved' ? '✓' : s.status === 'rejected' ? '✗' : s.level}
              </div>
              <div className="flex-1 text-sm">
                <div className="font-medium">
                  {s.level === 1 ? 'Qrup admini təsdiqi' : 'SuperAdmin təsdiqi'}
                </div>
                {s.status === 'approved' && u && (
                  <div className="text-xs text-emerald-700">
                    ✓ {u.full_name || u.username} • {new Date(s.approved_at).toLocaleString('az-AZ', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  </div>
                )}
                {s.status === 'rejected' && (
                  <div className="text-xs text-red-700">
                    ✗ Rədd: {(s.rejected_reason || '').slice(0, 50)}
                  </div>
                )}
                {s.status === 'pending' && (
                  <div className="text-[11px] text-zinc-500">
                    Namizədlər: {s.candidate_user_ids?.length || 0} nəfər
                    {(s.candidate_user_ids || []).slice(0, 3).map((id) => {
                      const cu = usersMap.get(id);
                      return cu ? ` • ${cu.full_name || cu.username}` : '';
                    }).join('')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action buttons (only if current user can approve current stage) */}
      {info.current_user_can_approve && info.current_stage && (
        <div className="border-t pt-3 space-y-2" data-testid="approval-actions">
          <div className="text-xs text-zinc-600 flex items-center gap-1">
            <Info className="h-3 w-3" />
            Mərhələ {info.current_stage.level}/2 — sizin təsdiqiniz gözlənilir
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approve-btn"
            >
              <CheckSquare className="h-4 w-4 mr-1" />
              {info.current_stage.level === 2 ? 'Təsdiqlə və arxivlə' : 'Təsdiq et'}
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="reject-btn"
            >
              <X className="h-4 w-4 mr-1" /> Rədd et
            </Button>
          </div>
        </div>
      )}

      {!info.current_user_can_approve && info.current_stage && (
        <div className="text-xs text-zinc-500 italic border-t pt-2">
          {info.current_stage.level === 1
            ? 'Qrup admininin təsdiqi gözlənilir'
            : 'SuperAdmin-in təsdiqi gözlənilir'}
          {isSA && info.current_stage.level === 1 && (
            <span className="block text-amber-600 mt-1">
              ℹ️ SuperAdmin kimi siz də bu mərhələni təsdiq edə bilərsiniz (override)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== Comments Thread ====================
function CommentsSection({ taskId, users }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tasks/${taskId}/comments`, { headers });
      setComments(r.data || []);
    } catch (e) {
      // silent
    }
  }, [taskId, headers]);

  useEffect(() => {
    if (taskId) fetchComments();
  }, [taskId, fetchComments]);

  const detectMentions = (text) => {
    const matches = [...text.matchAll(/@(\w+)/g)].map((m) => m[1]);
    const ids = [];
    for (const username of matches) {
      const u = users.find((x) => x.username === username);
      if (u) ids.push(u.id);
    }
    return ids;
  };

  const submit = async () => {
    if (!body.trim()) return;
    setLoading(true);
    try {
      const mentions = detectMentions(body);
      await axios.post(`${API}/tasks/${taskId}/comments`, { body: body.trim(), mentions }, { headers });
      setBody('');
      fetchComments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Şərh əlavə olunmadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t pt-3" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="h-4 w-4 text-purple-600" />
        <span className="text-sm font-medium">Şərhlər ({comments.length})</span>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {comments.length === 0 && (
          <div className="text-xs text-zinc-400 italic">Hələ şərh yoxdur</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="text-xs bg-zinc-50 rounded-md p-2" data-testid={`comment-${c.id}`}>
            <div className="font-semibold text-zinc-700">
              {c.author_name}
              <span className="text-[10px] font-normal text-zinc-500 ml-2">
                {new Date(c.created_at).toLocaleString('az-AZ', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
              </span>
            </div>
            <div className="text-zinc-800 mt-0.5 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Şərh yaz... (@username ilə qeyd et)"
          rows={2}
          className="text-sm"
          data-testid="comment-input"
        />
        <Button
          onClick={submit}
          disabled={loading || !body.trim()}
          size="sm"
          className="bg-purple-600 hover:bg-purple-700 self-end"
          data-testid="comment-submit"
        >
          Göndər
        </Button>
      </div>
    </div>
  );
}

// ==================== Task Dialog ====================
function TaskDialog({ open, task, users, allTasks, stages, canEdit, currentUserId, currentUser, onClose, onSave, onDelete, onApprovalChange }) {
  const [form, setForm] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [activeTab, setActiveTab] = useState('main');

  useEffect(() => {
    if (open && task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        status: task.status || stages[0]?.key || 'todo',
        priority: task.priority || 'medium',
        assignee_ids: task.assignee_ids || [],
        start_date: task.start_date || '',
        due_date: task.due_date || '',
        tags: task.tags || [],
        parent_id: task.parent_id || '',
      });
      setTagInput('');
      setAssigneeSearch('');
      setActiveTab('main');
    }
  }, [open, task, stages]);

  const usersMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const parentCandidates = useMemo(
    () => (allTasks || []).filter((x) => x.id !== task?.id && !x.parent_id),
    [allTasks, task?.id]
  );

  if (!form) return null;

  const isCreator = !task?.id || task?.created_by === currentUserId;
  const fullEditAllowed = canEdit || isCreator;

  const toggleAssignee = (id) =>
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(id)
        ? f.assignee_ids.filter((x) => x !== id)
        : [...f.assignee_ids, id],
    }));

  const addTag = () => {
    const v = tagInput.trim();
    if (!v || form.tags.includes(v)) {
      setTagInput('');
      return;
    }
    setForm({ ...form, tags: [...form.tags, v] });
    setTagInput('');
  };

  const filteredUsers = users.filter((u) => {
    if (!assigneeSearch) return true;
    const q = assigneeSearch.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q);
  });

  const handleSave = async () => {
    await onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{task?.id ? 'Tapşırıq redaktəsi' : 'Yeni tapşırıq'}</DialogTitle>
          <DialogDescription className="sr-only">Tapşırığın yaradılması və redaktəsi.</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        {task?.id && (
          <div className="flex gap-1 border-b -mx-6 px-6 -mt-2">
            <button
              onClick={() => setActiveTab('main')}
              className={`px-3 py-1.5 text-xs font-medium ${activeTab === 'main' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-zinc-600'}`}
              data-testid="task-tab-main"
            >
              Əsas
            </button>
            <button
              onClick={() => setActiveTab('approval')}
              className={`px-3 py-1.5 text-xs font-medium ${activeTab === 'approval' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-zinc-600'}`}
              data-testid="task-tab-approval"
            >
              Təsdiq
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`px-3 py-1.5 text-xs font-medium ${activeTab === 'comments' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-zinc-600'}`}
              data-testid="task-tab-comments"
            >
              Şərhlər
            </button>
          </div>
        )}

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {activeTab === 'main' && (
            <>
              <div>
                <label className="text-sm font-medium">Başlıq *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Tapşırığın adı"
                  data-testid="task-title-input"
                  disabled={!fullEditAllowed}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Açıqlama</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  disabled={!fullEditAllowed}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Etap</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {stages.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Prioritet</label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })} disabled={!fullEditAllowed}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Aşağı</SelectItem>
                      <SelectItem value="medium">Orta</SelectItem>
                      <SelectItem value="high">Yüksək</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Başlanma</label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} disabled={!fullEditAllowed} />
                </div>
                <div>
                  <label className="text-sm font-medium">Bitmə</label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} disabled={!fullEditAllowed} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Üst tapşırıq (parent)</label>
                <Select
                  value={form.parent_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, parent_id: v === 'none' ? '' : v })}
                  disabled={!fullEditAllowed}
                >
                  <SelectTrigger data-testid="task-parent-select">
                    <SelectValue placeholder="Yoxdur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yoxdur (root) —</SelectItem>
                    {parentCandidates.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Məsul şəxslər</label>
                <div className="relative mt-1">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-zinc-400" />
                  <Input
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    placeholder="İstifadəçi axtarışı..."
                    className="pl-7 text-sm h-8"
                    disabled={!fullEditAllowed}
                  />
                </div>
                <div className="border rounded-md p-2 max-h-36 overflow-y-auto space-y-1 mt-1">
                  {filteredUsers.length === 0 && <div className="text-xs text-zinc-400 italic">Heç nə tapılmadı</div>}
                  {filteredUsers.slice(0, 100).map((m) => {
                    const selected = form.assignee_ids.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-zinc-50 ${selected ? 'bg-purple-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleAssignee(m.id)}
                          disabled={!fullEditAllowed}
                          data-testid={`task-assignee-checkbox-${m.id}`}
                        />
                        <UserIcon className="h-3.5 w-3.5 text-zinc-400" />
                        <span className="text-sm flex-1 truncate">{m.full_name || m.username}</span>
                        <span className="text-[10px] text-zinc-500">@{m.username}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />
                  Təsdiq axını
                </label>
                <div className="text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                  ℹ️ Bu tapşırıq &laquo;Yoxlanılır&raquo; etabına çatdıqda **avtomatik** olaraq:
                  <br />• Mərhələ 1: icra edənin qrup admininin təsdiqi
                  <br />• Mərhələ 2: SuperAdmin-in təsdiqi və arxivləşdirməsi
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Etiketlər</label>
                <div className="flex gap-1 flex-wrap mt-1 mb-2">
                  {form.tags.map((t) => (
                    <Badge key={t} variant="secondary" className="gap-1">
                      {t}
                      {fullEditAllowed && (
                        <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })} className="hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
                {fullEditAllowed && (
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      placeholder="Etiket əlavə et və Enter"
                      className="text-sm"
                    />
                    <Button size="sm" variant="outline" onClick={addTag}>
                      <TagIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'approval' && task?.id && (
            <ApprovalActionPanel
              taskId={task.id}
              currentUser={currentUser}
              usersMap={usersMap}
              onAction={onApprovalChange}
            />
          )}

          {activeTab === 'comments' && task?.id && (
            <CommentsSection taskId={task.id} users={users} />
          )}
        </div>

        <DialogFooter className="gap-2">
          {fullEditAllowed && task?.id && (
            <Button
              variant="outline"
              onClick={() => onDelete(task)}
              className="text-red-600 hover:text-red-700 mr-auto"
              data-testid="task-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Sil
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Bağla</Button>
          {activeTab === 'main' && (
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700" data-testid="task-save-btn">
              Yadda saxla
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Main ====================
export default function TasksPanel({ user }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const isSuperAdmin = user?.role === 'superadmin' || user?.is_superadmin;

  const [tasks, setTasks] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, overdue_count: 0, progress_pct: 0 });
  const [loading, setLoading] = useState(false);

  // SuperAdmin god mode: default scope=all
  const [scope, setScope] = useState(isSuperAdmin ? 'all' : 'mine');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const fetchStages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/task-stages`, { headers });
      setStages(r.data || []);
    } catch (e) {
      // silent — fallback to empty stages
    }
  }, [headers]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      if (search) params.set('search', search);
      if (showArchived) params.set('include_archived', 'true');
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/tasks?${params.toString()}`, { headers }),
        axios.get(`${API}/tasks/stats?scope=${scope}`, { headers }),
      ]);
      setTasks(tRes.data || []);
      setStats(sRes.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yükləmə uğursuz');
    } finally {
      setLoading(false);
    }
  }, [scope, search, showArchived, headers]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, { headers });
      setUsers((r.data || []).filter((u) => u.is_active !== false));
    } catch {
      try {
        const r2 = await axios.get(`${API}/superadmin/users`, { headers });
        setUsers((r2.data || []).filter((u) => u.is_active !== false));
      } catch {
        setUsers([]);
      }
    }
  }, [headers]);

  useEffect(() => { fetchStages(); }, [fetchStages]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const usersMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const doneKeys = useMemo(
    () => new Set(stages.filter((s) => s.is_done).map((s) => s.key)),
    [stages]
  );

  // Stages visible on Kanban: hide archived by default
  const visibleStages = useMemo(
    () => stages.filter((s) => showArchived || !s.is_archived),
    [stages, showArchived]
  );

  const tasksByStatus = useMemo(() => {
    const map = {};
    stages.forEach((s) => {
      map[s.key] = tasks
        .filter((t) => t.status === s.key)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    });
    return map;
  }, [tasks, stages]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const targetStatus = destination.droppableId;
    const sourceCol = [...(tasksByStatus[source.droppableId] || [])];
    const destCol = source.droppableId === destination.droppableId ? sourceCol : [...(tasksByStatus[destination.droppableId] || [])];

    let moved;
    if (source.droppableId === destination.droppableId) {
      [moved] = sourceCol.splice(source.index, 1);
      sourceCol.splice(destination.index, 0, { ...moved, status: targetStatus });
    } else {
      [moved] = sourceCol.splice(source.index, 1);
      destCol.splice(destination.index, 0, { ...moved, status: targetStatus });
    }

    setTasks((prev) => {
      const others = prev.filter((t) => t.status !== source.droppableId && t.status !== destination.droppableId);
      const updatedSource = sourceCol.map((t, idx) => ({ ...t, position: idx }));
      const updatedDest = source.droppableId === destination.droppableId ? [] : destCol.map((t, idx) => ({ ...t, status: targetStatus, position: idx }));
      return [...others, ...updatedSource, ...updatedDest];
    });

    try {
      await axios.post(`${API}/tasks/${draggableId}/move`, { status: targetStatus, position: destination.index }, { headers });
      fetchTasks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Köçürmə uğursuz');
      fetchTasks();
    }
  };

  const handleAdd = (status) => {
    setEditingTask({ status, priority: 'medium' });
    setDialogOpen(true);
  };
  const handleCardClick = async (t) => {
    // Re-fetch single task to get fresh approvers/etc.
    try {
      const r = await axios.get(`${API}/tasks/${t.id}`, { headers });
      setEditingTask(r.data);
    } catch {
      setEditingTask(t);
    }
    setDialogOpen(true);
  };
  const handleSave = async (form) => {
    const payload = {
      ...form,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      parent_id: form.parent_id || null,
    };
    try {
      if (editingTask?.id) {
        const r = await axios.put(`${API}/tasks/${editingTask.id}`, payload, { headers });
        setEditingTask(r.data);
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/tasks`, payload, { headers });
        toast.success('Tapşırıq əlavə edildi');
        setDialogOpen(false);
        setEditingTask(null);
      }
      fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };
  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.title}" silinsin?`)) return;
    try {
      await axios.delete(`${API}/tasks/${t.id}`, { headers });
      toast.success('Silindi');
      setDialogOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const handleApprovalChange = async () => {
    if (!editingTask?.id) return;
    try {
      const r = await axios.get(`${API}/tasks/${editingTask.id}`, { headers });
      setEditingTask(r.data);
    } catch (e) {
      // silent
    }
    fetchTasks();
  };

  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  return (
    <div className="space-y-4" data-testid="tasks-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-purple-600" />
            Tapşırıqlar
            {isSuperAdmin && <Badge className="bg-purple-100 text-purple-700 text-[10px]">🛡️ SuperAdmin: bütün tapşırıqlar görünür</Badge>}
          </h2>
          <div className="text-xs text-zinc-500 mt-0.5">Layihədən kənar müstəqil tapşırıqların idarəedilməsi</div>
        </div>
        <Button onClick={() => handleAdd(stages.find((s) => s.is_initial)?.key || stages[0]?.key || 'todo')} className="bg-purple-600 hover:bg-purple-700" data-testid="new-task-btn">
          <Plus className="h-4 w-4 mr-1" /> Yeni tapşırıq
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Cəmi" value={stats.total || 0} color="bg-zinc-100 text-zinc-700" />
        <StatCard label="İcrada" value={(stats.by_status?.in_progress || 0) + (stats.by_status?.review || 0)} color="bg-blue-100 text-blue-700" />
        <StatCard label="Vaxtı keçmiş" value={stats.overdue_count || 0} color="bg-red-100 text-red-700" testid="stat-overdue" />
        <StatCard label="Tamamlanma %" value={`${stats.progress_pct || 0}%`} color="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-md border bg-white p-0.5" role="tablist">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`px-3 py-1 text-xs rounded transition ${scope === s.key ? 'bg-purple-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
              data-testid={`scope-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Başlıq üzrə axtarış..."
            className="pl-8 h-9 text-sm"
            data-testid="task-search-input"
          />
        </div>
        <button
          onClick={() => setShowArchived((v) => !v)}
          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border transition ${
            showArchived ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-50'
          }`}
          data-testid="toggle-show-archived"
        >
          <Archive className="h-3.5 w-3.5" />
          {showArchived ? 'Arxiv görünür' : 'Arxivi göstər'}
        </button>
        {loading && <span className="text-xs text-zinc-500">Yüklənir...</span>}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.max(visibleStages.length, 1)}, minmax(220px, 1fr))` }}
        >
          {visibleStages.map((stage) => (
            <Column
              key={stage.key}
              stage={stage}
              tasks={tasksByStatus[stage.key] || []}
              usersMap={usersMap}
              canAdd={true}
              onAdd={handleAdd}
              onCardClick={handleCardClick}
              doneKeys={doneKeys}
            />
          ))}
        </div>
      </DragDropContext>

      <TaskDialog
        open={dialogOpen}
        task={editingTask}
        users={users}
        allTasks={tasks}
        stages={stages}
        canEdit={isAdmin}
        currentUserId={user?.id}
        currentUser={user}
        onClose={() => { setDialogOpen(false); setEditingTask(null); }}
        onSave={handleSave}
        onDelete={handleDelete}
        onApprovalChange={handleApprovalChange}
      />
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={testid}>
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${color}`}>
        <ListChecks className="h-4 w-4" />
      </div>
      <div className="text-2xl font-bold mt-1.5 text-zinc-800">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </div>
  );
}
